// Live or preview? The portal only sends anything (answers, uploads, drafts, logins) when the page is on a live host.
// Everywhere else (PR previews, a local build) every form shows PREVIEW_MESSAGE, the send buttons are disabled and
// any call to the API is blocked here before it leaves the browser. PR builds also get a placeholder API address,
// so this is the second lock, not the only one.

// The hosts the live portal is served on: the Static Web App's own address (what families and planner links use
// today) and its custom domain assessments.clinicalminds.net (confirmed in Azure 20 Oct 2026, not yet in use).
// Add any other custom domain here (or in REACT_APP_LIVE_HOSTS, comma separated, at build time) only once it is
// confirmed in Azure.
export const DEFAULT_LIVE_HOSTS = ["brave-moss-0c7391300.3.azurestaticapps.net", "assessments.clinicalminds.net"];

export const PREVIEW_MESSAGE = "Preview: this form is switched off. It only sends on the live portal.";

// Never resolves (.invalid is reserved), so a stray call can't reach anything.
export const PLACEHOLDER_BACKEND_URL = "https://preview-api.invalid";

export function liveHosts(extra = process.env.REACT_APP_LIVE_HOSTS) {
  const more = String(extra || "")
    .split(/[,;\s]+/)
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  return [...DEFAULT_LIVE_HOSTS, ...more];
}

export function isLiveHost(hostname, hosts = liveHosts()) {
  const h = String(hostname || "").trim().toLowerCase().replace(/\.$/, "");
  return hosts.includes(h);
}

function currentHostname() {
  try {
    return window.location.hostname;
  } catch (e) {
    return "";
  }
}

export const IS_LIVE = isLiveHost(currentHostname());

// Sending is on only on a live host. Components read this to disable Submit, Send, Upload and Save draft.
export const SENDING_ON = IS_LIVE;

export const BACKEND_URL = IS_LIVE ? (process.env.REACT_APP_BACKEND_URL || "") : PLACEHOLDER_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export class PreviewBlockedError extends Error {
  constructor(url) {
    super(PREVIEW_MESSAGE);
    this.name = "PreviewBlockedError";
    this.previewBlocked = true;
    this.url = url;
  }
}

export function isPreviewBlocked(err) {
  return !!(err && (err.previewBlocked || (err.cause && err.cause.previewBlocked)));
}

// A request is an API call if it goes to /api/ (on any host), to an Azure Functions host, or to the placeholder.
export function isApiUrl(url) {
  let u;
  try {
    u = new URL(String(url), "http://page.local");
  } catch (e) {
    return true; // can't tell, so treat it as an API call
  }
  const host = u.hostname.toLowerCase();
  return (
    u.pathname === "/api" ||
    u.pathname.startsWith("/api/") ||
    host.endsWith(".azurewebsites.net") ||
    host.endsWith(".invalid")
  );
}

// Install the block. Call once, before the app renders. Does nothing on a live host.
export function installPreviewGuard({ live = IS_LIVE, axios, win = typeof window !== "undefined" ? window : null } = {}) {
  if (live || !win || win.__portalPreviewGuard) return false;
  win.__portalPreviewGuard = true;

  if (axios && axios.interceptors) {
    axios.interceptors.request.use((config) => {
      const full = config.baseURL && !/^https?:/i.test(config.url || "") ? `${config.baseURL}${config.url || ""}` : config.url;
      if (isApiUrl(full)) {
        return Promise.reject(new PreviewBlockedError(full));
      }
      return config;
    });
  }

  if (typeof win.fetch === "function") {
    const realFetch = win.fetch.bind(win);
    win.fetch = (input, init) => {
      const url = typeof input === "string" ? input : (input && input.url) || String(input);
      if (isApiUrl(url)) return Promise.reject(new PreviewBlockedError(url));
      return realFetch(input, init);
    };
  }

  if (win.XMLHttpRequest && win.XMLHttpRequest.prototype) {
    const proto = win.XMLHttpRequest.prototype;
    const realOpen = proto.open;
    const realSend = proto.send;
    proto.open = function (method, url, ...rest) {
      this.__portalBlocked = isApiUrl(url);
      return realOpen.call(this, method, url, ...rest);
    };
    proto.send = function (...args) {
      if (this.__portalBlocked) {
        throw new PreviewBlockedError("xhr");
      }
      return realSend.apply(this, args);
    };
  }

  if (win.navigator && typeof win.navigator.sendBeacon === "function") {
    const realBeacon = win.navigator.sendBeacon.bind(win.navigator);
    win.navigator.sendBeacon = (url, data) => (isApiUrl(url) ? false : realBeacon(url, data));
  }
  return true;
}
